import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './CalendarView.css';

const CalendarView = ({
  data = [],
  dateField = 'SpudDate',
  endDateField = null, // For date ranges
  title = 'Calendar View',
  cardFields = [], // Array of field objects: [{key: 'WellName', label: 'Well Name', primary: true}, ...]
  linkTemplate = '/wells/{id}', // Template for detail links, {id} will be replaced
  idField = 'WellID',
  onCardClick = null, // Optional custom click handler
  colorField = null, // Field to determine card color
  colorMap = {}, // Color mapping for colorField values
  defaultColor = '#007bff',
  monthOffset = 0 // For navigation between months
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'year-grid'
  const navigate = useNavigate();

  // Calculate display month based on offset
  const displayMonth = useMemo(() => {
    const date = new Date(currentMonth);
    date.setMonth(date.getMonth() + monthOffset);
    return date;
  }, [currentMonth, monthOffset]);

  // Get calendar grid for the current month
  const calendarDays = useMemo(() => {
    const year = displayMonth.getFullYear();
    const month = displayMonth.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    
    // Start from the beginning of the week containing the first day
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    // End at the end of the week containing the last day
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));
    
    const days = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  }, [displayMonth]);

  // Parse date string to Date object
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  };

  // Check if two dates are the same day
  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.toDateString() === date2.toDateString();
  };

  // Get items for a specific date
  const getItemsForDate = (date) => {
    return data.filter(item => {
      const itemDate = parseDate(item[dateField]);
      if (!itemDate) return false;
      
      // For single date items
      if (!endDateField || !item[endDateField]) {
        return isSameDay(itemDate, date);
      }
      
      // For date range items
      const endDate = parseDate(item[endDateField]);
      if (!endDate) return isSameDay(itemDate, date);
      
      return date >= itemDate && date <= endDate;
    });
  };

  // Get date range spans for multi-day items
  const getDateRangeSpans = useMemo(() => {
    if (!endDateField) return [];
    
    const spans = [];
    
    data.forEach(item => {
      const startDate = parseDate(item[dateField]);
      const endDate = parseDate(item[endDateField]);
      
      if (!startDate || !endDate || startDate >= endDate) return;
      
      // Check if the range intersects with current month view
      const monthStart = calendarDays[0];
      const monthEnd = calendarDays[calendarDays.length - 1];
      
      if (endDate < monthStart || startDate > monthEnd) return;
      
      // Calculate position within the calendar grid
      const rangeStart = startDate < monthStart ? monthStart : startDate;
      const rangeEnd = endDate > monthEnd ? monthEnd : endDate;
      
      const startIndex = calendarDays.findIndex(day => isSameDay(day, rangeStart));
      const endIndex = calendarDays.findIndex(day => isSameDay(day, rangeEnd));
      
      if (startIndex !== -1 && endIndex !== -1) {
        spans.push({
          item,
          startIndex,
          endIndex,
          startRow: Math.floor(startIndex / 7),
          endRow: Math.floor(endIndex / 7),
          color: colorField && item[colorField] && colorMap[item[colorField]] 
            ? colorMap[item[colorField]] 
            : defaultColor
        });
      }
    });
    
    return spans;
  }, [data, dateField, endDateField, calendarDays, colorField, colorMap, defaultColor]);

  // Navigate months
  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  // Navigate to specific month/year
  const navigateToDate = (year, month) => {
    const newDate = new Date(year, month, 1);
    setCurrentMonth(newDate);
    setViewMode('month');
  };

  // Handle card click
  const handleCardClick = (item, event) => {
    if (onCardClick) {
      onCardClick(item, event);
    }
  };

  // Handle indicator click to navigate directly to link
  const handleIndicatorClick = (item, event) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Generate link URL and navigate
    const link = linkTemplate.replace('{id}', item[idField]);
    navigate(link);
  };

  // Generate link URL
  const generateLink = (item) => {
    return linkTemplate.replace('{id}', item[idField]);
  };

  // Get card color
  const getCardColor = (item) => {
    if (colorField && item[colorField] && colorMap[item[colorField]]) {
      return colorMap[item[colorField]];
    }
    return defaultColor;
  };

  // Render a compact indicator
  const renderIndicator = (item) => {
    const itemId = item[idField];
    const cardColor = getCardColor(item);
    const primaryField = cardFields.find(f => f.primary) || cardFields[0];
    const primaryValue = primaryField ? item[primaryField.key] : itemId;
    
    return (
      <div
        key={itemId}
        className="calendar-indicator"
        style={{ '--indicator-color': cardColor }}
        onClick={(e) => handleIndicatorClick(item, e)}
        title={primaryValue}
      >
        <span className="indicator-text">{primaryValue}</span>
      </div>
    );
  };

  // Render year grid view
  const renderYearGrid = () => {
    const currentYear = displayMonth.getFullYear();
    const months = Array.from({ length: 12 }, (_, i) => i);
    
    return (
      <div className="year-grid">
        <div className="year-grid-header">
          <button 
            onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear() - 1, prev.getMonth(), 1))}
            title="Previous Year"
          >
            ‹‹
          </button>
          <h3>Year {currentYear}</h3>
          <button 
            onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear() + 1, prev.getMonth(), 1))}
            title="Next Year"
          >
            ››
          </button>
        </div>
        
        <div className="months-grid">
          {months.map(month => {
            const monthData = data.filter(item => {
              const itemDate = parseDate(item[dateField]);
              return itemDate && itemDate.getFullYear() === currentYear && itemDate.getMonth() === month;
            });
            
            return (
              <div
                key={month}
                className={`month-cell ${month === displayMonth.getMonth() ? 'current-month' : ''}`}
                onClick={() => navigateToDate(currentYear, month)}
              >
                <div className="month-name">
                  {monthNames[month].substring(0, 3)}
                </div>
                <div className="month-data-count">
                  {monthData.length} {idField}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="calendar-view">
      <div className="calendar-header">
        <div className="calendar-title">
          <h2>{title}</h2>
          <div className="data-count">
            {data.length} {data.length === 1 ? 'item' : 'items'}
          </div>
        </div>
        
        <div className="calendar-navigation">
          {viewMode === 'month' ? (
            <>
              <div className="year-nav">
                <button 
                  className="nav-btn year-nav-btn"
                  onClick={() => {
                    const newDate = new Date(currentMonth);
                    newDate.setFullYear(newDate.getFullYear() - 1);
                    setCurrentMonth(newDate);
                  }}
                  title="Previous Year"
                >
                  ‹‹
                </button>
              </div>
              
              <button 
                className="nav-btn"
                onClick={() => navigateMonth(-1)}
                title="Previous Month"
              >
                ‹
              </button>
              
              <div 
                className="current-month clickable"
                onClick={() => setViewMode('year-grid')}
                title="Click to select month/year"
              >
                {monthNames[displayMonth.getMonth()]} {displayMonth.getFullYear()}
              </div>
              
              <button 
                className="nav-btn"
                onClick={() => navigateMonth(1)}
                title="Next Month"
              >
                ›
              </button>
              
              <div className="year-nav">
                <button 
                  className="nav-btn year-nav-btn"
                  onClick={() => {
                    const newDate = new Date(currentMonth);
                    newDate.setFullYear(newDate.getFullYear() + 1);
                    setCurrentMonth(newDate);
                  }}
                  title="Next Year"
                >
                  ››
                </button>
              </div>
            </>
          ) : (
            <button 
              className="back-to-month-btn"
              onClick={() => setViewMode('month')}
            >
              ← Back to Calendar
            </button>
          )}
        </div>

        <div className="view-controls">
          {viewMode === 'month' && (
            <>
              <button 
                className="today-btn"
                onClick={() => setCurrentMonth(new Date())}
              >
                Today
              </button>
              <button 
                className="year-grid-btn"
                onClick={() => setViewMode('year-grid')}
              >
                Year View
              </button>
            </>
          )}
        </div>
      </div>

      {viewMode === 'month' ? (
        <div className="calendar-grid">
          {/* Day headers */}
          <div className="calendar-days-header">
            {dayNames.map(day => (
              <div key={day} className="day-header">{day}</div>
            ))}
          </div>

          {/* Calendar body with date range spans */}
          <div className="calendar-body">
            {/* Date range spans (background) */}
            {getDateRangeSpans.map((span, index) => (
              <div
                key={`span-${index}`}
                className="date-range-span"
                style={{
                  '--span-color': span.color,
                  '--start-col': (span.startIndex % 7) + 1,
                  '--end-col': (span.endIndex % 7) + 2,
                  '--start-row': span.startRow + 1,
                  '--end-row': span.endRow + 1,
                  gridColumnStart: (span.startIndex % 7) + 1,
                  gridColumnEnd: (span.endIndex % 7) + 2,
                  gridRowStart: span.startRow + 1,
                  gridRowEnd: span.endRow + 2
                }}
                title={`${span.item[cardFields[0]?.key || idField]} (${parseDate(span.item[dateField])?.toLocaleDateString()} - ${parseDate(span.item[endDateField])?.toLocaleDateString()})`}
                onClick={(e) => handleIndicatorClick(span.item, e)}
              >
                <div className="range-label">
                  {span.item[cardFields[0]?.key || idField]}
                </div>
              </div>
            ))}

            {/* Calendar days */}
            {calendarDays.map((day, index) => {
              const isCurrentMonth = day.getMonth() === displayMonth.getMonth();
              const isToday = isSameDay(day, new Date());
              const dayItems = getItemsForDate(day);
              const singleDateItems = dayItems.filter(item => 
                !endDateField || !item[endDateField] || 
                parseDate(item[dateField])?.toDateString() === parseDate(item[endDateField])?.toDateString()
              );

              return (
                <div
                  key={index}
                  className={`calendar-day compact ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''}`}
                >
                  <div className="day-number">{day.getDate()}</div>
                  
                  <div className="day-indicators">
                    {singleDateItems.map(item => renderIndicator(item))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        renderYearGrid()
      )}

      {/* Legend */}
      {colorField && Object.keys(colorMap).length > 0 && (
        <div className="calendar-legend">
          <div className="legend-title">Legend:</div>
          <div className="legend-items">
            {Object.entries(colorMap).map(([key, color]) => (
              <div key={key} className="legend-item">
                <div 
                  className="legend-color" 
                  style={{ backgroundColor: color }}
                ></div>
                <span className="legend-label">{key}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView; 